import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileX, Check, ExternalLink } from "lucide-react";
import { apiService } from "../services";
import { Book } from "../types";
import {
  useCart,
  formatBookPrice,
  isHorsSerie,
} from "../context";
import { PageHeader, CartWidget } from "../components";

/**
 * Les titres du catalogue suivent la convention "Titre français / Titl brezhoneg".
 * On isole le titre breton (mis en avant) du titre français (discret).
 */
const splitTitle = (title: string): { breton: string; french?: string } => {
  const separatorIndex = title.indexOf(" / ");
  if (separatorIndex === -1) return { breton: title };
  return {
    french: title.slice(0, separatorIndex).trim(),
    breton: title.slice(separatorIndex + 3).trim(),
  };
};

export const EditionDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isInCart, toggleItem } = useCart();

  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchBook = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getBook(parseInt(id, 10));
        if (!cancelled) setBook(data);
      } catch (err) {
        console.error("Error fetching book:", err);
        if (!cancelled) setError(t("edition.load_error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBook();
    return () => {
      cancelled = true;
    };
  }, [id, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-church-light flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-church-brown mx-auto mb-4"></div>
        </div>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-xl border border-gray-200 p-12 max-w-md mx-4">
          <FileX className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-2xl font-semibold text-gray-900 mb-4 font-sans">
            {t("edition.list.empty")}
          </h2>
          <button
            onClick={() => navigate("/edition")}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-church-brown to-church-dark text-white rounded-lg hover:from-church-dark hover:to-church-brown transition-all duration-200 font-medium shadow-md hover:shadow-lg font-sans"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t("edition.hero_title")}
          </button>
        </div>
      </div>
    );
  }

  const { breton, french } = splitTitle(book.title);
  const isSpecial = isHorsSerie(book);
  const selected = isInCart(book);
  const canBuy = book.price != null;

  return (
    <div className="min-h-screen">
      <PageHeader title={breton} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="p-8 md:p-12">
            <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
              {/* Couverture */}
              <div className="md:w-2/5 lg:w-1/3 flex-shrink-0">
                <div className="relative rounded-xl overflow-hidden shadow-lg bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center min-h-[18rem] sticky top-24">
                  {book.cover_image ? (
                    <img
                      src={apiService.getBookCoverUrl(book.id)}
                      alt={book.title}
                      className="w-full h-auto object-contain"
                    />
                  ) : (
                    <svg
                      className="w-24 h-24 text-slate-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                      />
                    </svg>
                  )}
                </div>
              </div>

              {/* Infos */}
              <div className="flex-1 min-w-0">
                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  {isSpecial ? (
                    <span className="bg-rose-600 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full">
                      {t("edition.sections.special")}
                    </span>
                  ) : (
                    book.edition_num && (
                      <span className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                        N° {book.edition_num}
                      </span>
                    )
                  )}
                  <span className="bg-amber-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                    {formatBookPrice(book)}
                  </span>
                </div>

                <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 leading-snug">
                  {breton}
                </h1>
                {french && (
                  <p className="text-base text-gray-400 mt-1">{french}</p>
                )}

                {/* Métadonnées */}
                <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  {book.author && (
                    <div>
                      <dt className="text-gray-500">{t("edition.detail.author")}</dt>
                      <dd className="text-gray-800 font-medium">{book.author}</dd>
                    </div>
                  )}
                  {book.year != null && (
                    <div>
                      <dt className="text-gray-500">{t("edition.detail.year")}</dt>
                      <dd className="text-gray-800 font-medium">{book.year}</dd>
                    </div>
                  )}
                  {book.publisher && (
                    <div>
                      <dt className="text-gray-500">{t("edition.detail.publisher")}</dt>
                      <dd className="text-gray-800 font-medium">{book.publisher}</dd>
                    </div>
                  )}
                  {book.isbn && (
                    <div>
                      <dt className="text-gray-500">ISBN</dt>
                      <dd className="text-gray-800 font-medium">{book.isbn}</dd>
                    </div>
                  )}
                </dl>

                {/* Description */}
                {book.description && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                      {book.description}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="mt-8 flex flex-wrap gap-3">
                  {canBuy && (
                    <button
                      onClick={() => toggleItem(book)}
                      className={`px-6 py-3 rounded-lg text-sm font-medium transition inline-flex items-center justify-center gap-1.5 ${
                        selected
                          ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                          : "bg-amber-600 text-white hover:bg-amber-700"
                      }`}
                    >
                      {selected ? (
                        <>
                          <Check className="w-4 h-4" /> {t("edition.detail.in_cart")}
                        </>
                      ) : (
                        t("edition.detail.add_to_cart")
                      )}
                    </button>
                  )}
                  {book.external_link && (
                    <a
                      href={book.external_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-6 py-3 rounded-lg text-sm font-medium transition inline-flex items-center justify-center gap-1.5 border border-amber-600 text-amber-700 hover:bg-amber-50"
                    >
                      <ExternalLink className="w-4 h-4" /> {t("edition.list.link")}
                    </a>
                  )}
                </div>

                {/* Retour */}
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <button
                    onClick={() => navigate("/edition")}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-church-brown to-church-dark text-white rounded-lg hover:from-church-dark hover:to-church-brown transition-all duration-200 font-medium shadow-md hover:shadow-lg font-sans"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t("edition.detail.back")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CartWidget />
    </div>
  );
};
